import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import useAuthStore from "./auth";

export interface VPaymentWidgetConfig {
	token: string;
	merchantId: string;
	onSuccess?: (response: { method: string; reference: string }) => void;
	onClose?: () => void;
	environment?: "production" | "development";
	logo?: string;
	email: string;
}

interface VPaymentWidgetOptions extends Omit<VPaymentWidgetConfig, "email" | "onSuccess" | "onClose"> {
	amount: number;
	onSuccess: (response: { method: string; reference: string }) => void;
	onClose?: () => void;
}

declare global {
	interface Window {
		VPaymentWidget: new (config: VPaymentWidgetConfig) => {
			pay: (params: { amount: number }) => Promise<void>;
			close: () => void;
		};
	}
}

interface UseVPaymentWidgetResult {
	isInitialized: boolean;
	initializePayment: (options: VPaymentWidgetOptions) => void;
}

export function useVPaymentWidget(): UseVPaymentWidgetResult {
	const [isInitialized, setIsInitialized] = useState(false);
	const [widget, setWidget] = useState<InstanceType<typeof window.VPaymentWidget> | null>(null);

	const user = useAuthStore(state => state.user);
	if (!user)
		throw new Error("User not found");

	useEffect(() => {
		const script = document.createElement("script");
		script.src = "https://swiftpay-sdk.vfdtech.ng/v-payment-widget.js";
		script.async = true;
		script.onload = () => setIsInitialized(true);
		script.onerror = () => {
			console.error("Failed to load VPaymentWidget script.");
			toast.error("Failed to load payment widget.");
		};
		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	const initializePayment = useCallback(
		({ token, merchantId, logo, environment = "development", onSuccess, amount, onClose }: VPaymentWidgetOptions) => {
			if (!isInitialized)
				throw new Error("VPaymentWidget not initialized");

			if (!user)
				throw new Error("User not found");

			const { email } = user;

			const config: VPaymentWidgetConfig = {
				token,
				merchantId,
				logo,
				environment,
				email,
				onSuccess: (response: { method: string; reference: string }) => {
					onSuccess(response);
					widget?.close();
				},
				onClose: () => {
					onClose && onClose();
				}

			};

			const newWidget = new window.VPaymentWidget(config);
			setWidget(newWidget);
			newWidget.pay({ amount }).catch((err: any) => {
				console.error("Error initializing payment:", err);
				toast.error("Failed to initialize payment.");
			});
			import.meta.env.DEV && window.open(`${window.location.origin}/test-payment`, "_blank");
		},
		[isInitialized, user, widget]
	);

	return { initializePayment, isInitialized };
}
